/**
 * Faction Manager - tracks faction states and relationships
 */

import type { FactionId } from '@/types';
import { FACTIONS } from './FactionData';

export interface FactionState {
  id: FactionId;
  gold: number;
  territoryCount: number;
  unitCount: number;
  buildingCount: number;
  isDefeated: boolean;
  isPlayer: boolean;
}

// Callback type to check for non-aggression treaties
type TreatyChecker = (a: FactionId, b: FactionId) => boolean;

export class FactionManager {
  private factions: Map<FactionId, FactionState> = new Map();
  private relations: Map<string, number> = new Map();
  private hasNonAggressionTreaty: TreatyChecker | null = null;

  constructor() {
    this.initializeFactions();
  }

  /**
   * Set a callback to check for non-aggression treaties
   * This is called by DiplomacySystem after initialization
   */
  setTreatyChecker(checker: TreatyChecker): void {
    this.hasNonAggressionTreaty = checker;
  }

  private initializeFactions(): void {
    for (const faction of Object.values(FACTIONS)) {
      this.factions.set(faction.id, {
        id: faction.id,
        gold: 500,
        territoryCount: 0,
        unitCount: 0,
        buildingCount: 0,
        isDefeated: false,
        isPlayer: false,
      });
    }

    // Set initial relationships
    this.setRelation('yan', 'han', -80);  // Historical enemies
    this.setRelation('shang', 'xuan', 60); // Trade partners
    this.setRelation('lin', 'tie', -40);  // Border dispute
  }

  private getRelationKey(a: FactionId, b: FactionId): string {
    return a < b ? `${a}:${b}` : `${b}:${a}`;
  }

  setRelation(a: FactionId, b: FactionId, value: number): void {
    const key = this.getRelationKey(a, b);
    this.relations.set(key, Math.max(-100, Math.min(100, value)));
  }

  getRelation(a: FactionId, b: FactionId): number {
    if (a === b) return 100;
    const key = this.getRelationKey(a, b);
    return this.relations.get(key) ?? 0;
  }

  isEnemy(a: FactionId, b: FactionId): boolean {
    if (a === b) return false;
    if (a === 'neutral' || b === 'neutral') return false;

    // Check for non-aggression treaty - if exists, they are not enemies
    if (this.hasNonAggressionTreaty && this.hasNonAggressionTreaty(a, b)) {
      return false;
    }

    return this.getRelation(a, b) < -20;
  }

  getFactionState(id: FactionId): FactionState | undefined {
    return this.factions.get(id);
  }

  setPlayerFaction(id: FactionId): void {
    for (const state of this.factions.values()) {
      state.isPlayer = state.id === id;
    }
  }

  getPlayerFaction(): FactionId | null {
    for (const state of this.factions.values()) {
      if (state.isPlayer) return state.id;
    }
    return null;
  }

  addGold(id: FactionId, amount: number): void {
    const state = this.factions.get(id);
    if (state) {
      state.gold += amount;
    }
  }

  spendGold(id: FactionId, amount: number): boolean {
    const state = this.factions.get(id);
    if (state && state.gold >= amount) {
      state.gold -= amount;
      return true;
    }
    return false;
  }

  getGold(id: FactionId): number {
    return this.factions.get(id)?.gold ?? 0;
  }

  updateCounts(id: FactionId, units: number, buildings: number, territory: number): void {
    const state = this.factions.get(id);
    if (state) {
      state.unitCount = units;
      state.buildingCount = buildings;
      state.territoryCount = territory;
    }
  }

  setDefeated(id: FactionId): void {
    const state = this.factions.get(id);
    if (state) {
      state.isDefeated = true;
    }
  }

  getActiveFactions(): FactionId[] {
    const active: FactionId[] = [];
    for (const state of this.factions.values()) {
      if (!state.isDefeated && state.id !== 'neutral') {
        active.push(state.id);
      }
    }
    return active;
  }

  getAllFactionStates(): FactionState[] {
    return Array.from(this.factions.values());
  }
}
